import { useSyncExternalStore } from "react";
import { create } from "zustand";

interface ConnectionState {
  /** The last request's transport failed outright; any response, even a 500, clears it. */
  serverUnreachable: boolean;
  markReachable: () => void;
  markUnreachable: () => void;
}

/** Setters return the same object when unchanged so zustand skips listeners; `markReachable` runs on every response. */
export const useConnectionStore = create<ConnectionState>((set) => ({
  serverUnreachable: false,
  markReachable: () =>
    set((s) => (s.serverUnreachable ? { serverUnreachable: false } : s)),
  markUnreachable: () =>
    set((s) => (s.serverUnreachable ? s : { serverUnreachable: true })),
}));

const subscribeToBrowserOnline = (onChange: () => void) => {
  window.addEventListener("online", onChange);
  window.addEventListener("offline", onChange);
  return () => {
    window.removeEventListener("online", onChange);
    window.removeEventListener("offline", onChange);
  };
};

/**
 * Not react-query's `onlineManager`, which assumes online until an `offline` event a tab opened offline never sees.
 * False is trustworthy, true is not: a dead line behind a live router still reads online.
 */
export const useBrowserOnline = (): boolean =>
  useSyncExternalStore(
    subscribeToBrowserOnline,
    () => navigator.onLine,
    () => true,
  );
